import { useState, useEffect } from 'react'
import { createClient } from '@supabase/supabase-js'
import { useAuth } from '../contexts/AuthContext'

// Configuração do Supabase
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const supabase = createClient(supabaseUrl, supabaseKey)

interface Plano {
    id: string
    nome: string
    nome_display: string
    descricao: string
    preco_mensal: number
    periodo_trial: number
    recursos: string[]
    limite_vendas?: number
    limite_corretores?: number
    limite_imoveis?: number
    suporte_prioritario: boolean
    relatorios_avancados: boolean
    api_access: boolean
}

interface Assinatura {
    id: string
    user_id: string
    plano_id: string
    afiliado_id?: string
    status: 'trial' | 'ativa' | 'cancelada' | 'expirada' | 'suspensa'
    data_inicio: string
    data_fim_trial?: string
    data_proximo_pagamento?: string
    data_cancelamento?: string
    valor_mensal: number
    ciclo_pagamento: 'mensal' | 'anual'
    metodo_pagamento?: string
    comissao_paga: boolean
    valor_comissao?: number
    observacoes?: string
    created_at: string
    updated_at: string
    plano: Plano
}

interface AssinaturaStatus {
    status: 'trial' | 'ativa' | 'cancelada' | 'expirada' | 'suspensa' | 'sem_assinatura'
    dias_restantes_trial?: number
    trial_expirado: boolean
    proximo_pagamento?: string
}

export function useAssinatura() {
    const { user } = useAuth()
    const [assinatura, setAssinatura] = useState<Assinatura | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const loadAssinatura = async () => {
        if (!user?.id) {
            setLoading(false)
            return
        }

        try {
            setLoading(true)
            setError(null)

            const { data, error } = await supabase
                .from('assinaturas')
                .select(`
                    *,
                    plano:planos(*)
                `)
                .eq('user_id', user.id)
                .order('created_at', { ascending: false })
                .limit(1)
                .single()

            if (error && error.code !== 'PGRST116') {
                throw error
            }

            setAssinatura(data || null)
        } catch (err) {
            console.error('Erro ao carregar assinatura:', err)
            setError(err instanceof Error ? err.message : 'Erro ao carregar assinatura')
        } finally {
            setLoading(false)
        }
    }

    const getAssinaturaStatus = (): AssinaturaStatus => {
        if (!assinatura) {
            return {
                status: 'sem_assinatura',
                trial_expirado: false
            }
        }

        const now = new Date()
        const dataFimTrial = assinatura.data_fim_trial ? new Date(assinatura.data_fim_trial) : null
        const diasRestantes = dataFimTrial
            ? Math.ceil((dataFimTrial.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
            : 0

        // Se está em trial e ainda não expirou
        if (assinatura.status === 'trial' && dataFimTrial && dataFimTrial > now) {
            return {
                status: 'trial',
                dias_restantes_trial: diasRestantes,
                trial_expirado: false
            }
        }

        // Se está em trial mas expirou
        if (assinatura.status === 'trial' && dataFimTrial && dataFimTrial <= now) {
            return {
                status: 'trial',
                dias_restantes_trial: 0,
                trial_expirado: true
            }
        }

        // Se está ativa
        if (assinatura.status === 'ativa') {
            return {
                status: 'ativa',
                trial_expirado: false,
                proximo_pagamento: assinatura.data_proximo_pagamento
            }
        }

        // Outros status
        return {
            status: assinatura.status,
            trial_expirado: false
        }
    }

    const cancelarAssinatura = async (motivo?: string) => {
        if (!assinatura?.id) return

        try {
            const { error } = await supabase
                .rpc('cancelar_assinatura', {
                    p_assinatura_id: assinatura.id,
                    p_motivo: motivo
                })

            if (error) throw error

            // Recarregar assinatura
            await loadAssinatura()
            return true
        } catch (err) {
            console.error('Erro ao cancelar assinatura:', err)
            setError(err instanceof Error ? err.message : 'Erro ao cancelar assinatura')
            return false
        }
    }

    const ativarAssinatura = async () => {
        if (!assinatura?.id) return

        try {
            const { error } = await supabase
                .rpc('ativar_assinatura', {
                    p_assinatura_id: assinatura.id
                })

            if (error) throw error

            // Recarregar assinatura
            await loadAssinatura()
            return true
        } catch (err) {
            console.error('Erro ao ativar assinatura:', err)
            setError(err instanceof Error ? err.message : 'Erro ao ativar assinatura')
            return false
        }
    }

    const verificarLimites = (tipo: 'vendas' | 'corretores' | 'imoveis', quantidadeAtual: number) => {
        if (!assinatura?.plano) return { permitido: true, limite: null }

        const plano = assinatura.plano
        let limite: number | null = null

        switch (tipo) {
            case 'vendas':
                limite = plano.limite_vendas || null
                break
            case 'corretores':
                limite = plano.limite_corretores || null
                break
            case 'imoveis':
                limite = plano.limite_imoveis || null
                break
        }

        if (limite === null) {
            return { permitido: true, limite: null }
        }

        return {
            permitido: quantidadeAtual < limite,
            limite,
            restantes: limite - quantidadeAtual
        }
    }

    const temRecurso = (recurso: 'suporte_prioritario' | 'relatorios_avancados' | 'api_access') => {
        if (!assinatura?.plano) return false
        return assinatura.plano[recurso] === true
    }

    useEffect(() => {
        loadAssinatura()
    }, [user?.id])

    return {
        assinatura,
        loading,
        error,
        loadAssinatura,
        getAssinaturaStatus,
        cancelarAssinatura,
        ativarAssinatura,
        verificarLimites,
        temRecurso
    }
}
